/**
 * [[ entity search suggestion configuration for the TipTap editor.
 *
 * - Trigger: `[[`
 * - API: GET /api/entity-search?q=<query>
 * - On selection: inserts an inline entity-link node with type, id, and label
 */

import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionOptions } from '@tiptap/suggestion';
import { apiGet } from '../../lib/api';
import { createSuggestionRenderer } from './suggestion-renderer';
import type { SuggestionItem } from './SuggestionList';

interface EntitySearchResult {
  entity_type: string;
  entity_id: string;
  name: string;
}

export const EntityLinkPluginKey = new PluginKey('entityLink');

export const entityLinkSuggestion: Omit<SuggestionOptions<SuggestionItem>, 'editor'> = {
  char: '[[',
  pluginKey: EntityLinkPluginKey,
  allowSpaces: true,

  items: async ({ query }) => {
    if (!query || query.length === 0) {
      try {
        const results = await apiGet<EntitySearchResult[]>('/entity-search?q=&limit=10');
        return results.map(entityToItem);
      } catch {
        return [];
      }
    }

    try {
      const results = await apiGet<EntitySearchResult[]>(
        `/entity-search?q=${encodeURIComponent(query)}&limit=10`,
      );
      return results.map(entityToItem);
    } catch {
      return [];
    }
  },

  command: ({ editor, range, props: item }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent({
        type: 'entityLink',
        attrs: {
          entityType: item.secondary,  // we store entity_type in secondary
          entityId: item.id,
          label: item.label,
        },
      })
      .run();
  },

  render: createSuggestionRenderer({ variant: 'entity' }),
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  task: 'Task',
  meeting_instance: 'Meeting',
  meeting_series: 'Series',
  reference_doc: 'View',
  person: 'Person',
  tag: 'Tag',
  scope: 'Project',
  quick_capture: 'Capture',
};

function entityToItem(entity: EntitySearchResult): SuggestionItem {
  return {
    id: entity.entity_id,
    label: entity.name,
    secondary: entity.entity_type,
    // Use entityType for display label in suggestion list
    color: ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type,
  };
}
